import React, { useState, ChangeEvent, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import MaterialTable, { Action } from 'material-table';
import { Button, Paper, InputLabel, Link, List, OutlinedInput } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';

import { DateTimeRange } from '../../../shared/components/DateTime/DateTimeRangePicker';
import CustomAndRelativeDateTimeRangePicker from '../../../shared/components/DateTime/CustomAndRelativeDateTimeRangePicker';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../../shared/config/snackConfig';
import { EnqueueJobRequest, Job, submitJob } from '../../../shared/services/job';
import { mainStyles } from '../../../shared/styles/common';
import { round } from '../../../shared/utils/math';
import { promStringifyWithName } from '../../../shared/utils/prometheus';
import Hover from './Hover';
import OnlineMetricsJob, { MetricsListData, MetricsTableRowData } from './OnlineMetricsJob';
import OnlineMetricsJobResults from './OnlineMetricsJobResults';
import TimeseriesExpression from './TimeseriesExpression';

const useStyles = makeStyles((theme: Theme) => ({
  overview: {
    padding: '10px',
    marginBottom: '10px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  buttonGroup: {
    margin: theme.spacing(1),
    width: '25%',
    display: 'grid',
    gridTemplateAreas: `
      'button1 button2'
`,
    gridTemplateColumns: 'auto auto',
    columnGap: theme.spacing(1),
  },
  button1: {
    gridArea: 'button1',
  },
  button2: {
    gridArea: 'button2',
  },
  title: {
    textAlign: 'center',
  },
  spacing: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

export interface RelevanceRankingJobResult {
  error?: string;
  result?: {
    name: string;
    labels: Record<string, string>;
    score?: number;
    error?: string;
  }[];
}

const getIssue = (rowData: MetricsTableRowData) => {
  const { primaryName, primaryMetricSelectors } = rowData;
  const labels = JSON.parse(primaryMetricSelectors) as Record<string, string>;
  return promStringifyWithName(primaryName, labels);
};

const getMinMax = (results: RelevanceRankingJobResult['result']) => {
  if (results!.length === 0) {
    return {
      min: undefined,
      max: undefined,
    };
  }
  const min = !results![0].error ? results![0].score : undefined;
  let max: number | undefined;
  for (let i = results!.length - 1; i >= 0; i -= 1) {
    if (!results![i].error) {
      max = results![i].score;
      break;
    }
  }
  return { min, max };
};

const getPerc = (value: number, min: number | undefined, max: number | undefined): string | undefined => {
  if (typeof min === 'undefined' || typeof max === 'undefined') {
    return undefined;
  }
  if (min === max) {
    return '100.00%';
  }
  return `${round(((max - value) / (max - min)) * 100)}%`;
};

export const transformResults = (job: Job): MetricsListData => {
  const listData: MetricsListData = {};
  const jobResults = job.results as RelevanceRankingJobResult;
  if (jobResults && jobResults.result) {
    const { min, max } = getMinMax(jobResults.result);
    listData['0'] = jobResults.result.map(({ name, labels, score, error }) => ({
      primaryName: name.trim(),
      primaryMetricSelectors: JSON.stringify(labels),
      value: error ? 'FAILED' : getPerc(score!, min, max) || 'N/A',
      hover: !error ? score : undefined,
    }));
  }
  return listData;
};

export const columns = [
  {
    title: 'Timeseries',
    type: 'string',
    cellStyle: {
      width: '100%',
    },
    // eslint-disable-next-line react/display-name
    render: ({ primaryName, primaryMetricSelectors }: MetricsTableRowData): React.ReactNode => (
      <TimeseriesExpression name={primaryName} labels={primaryMetricSelectors} />
    ),
  },
  {
    title: 'Score',
    type: 'numeric',
    // eslint-disable-next-line react/display-name
    render: ({ value, hover }: MetricsTableRowData): React.ReactNode => <Hover value={value} hover={hover} />,
  },
];

export const actions: MaterialTable<MetricsTableRowData>['props']['actions'] = [
  (rowData: MetricsTableRowData): Action<MetricsTableRowData> => {
    return {
      // eslint-disable-next-line react/display-name
      icon: () => (
        <Link
          to={`/similarityRanking?issue=${getIssue(rowData)}`}
          component={RouterLink}
          underline="none"
          color="inherit"
        >
          <FontAwesomeIcon icon={faBullseye} />
        </Link>
      ),
      tooltip: 'Run Recommendation',
      onClick: () => {},
    };
  },
];

const JOB_TYPE = 'RELEVANCE_RANKING';

const RelevanceRanking: React.FC = () => {
  const classes = { ...mainStyles(), ...useStyles() };

  const [selectors, setSelectors] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<DateTimeRange>({
    start: moment().startOf('minute').subtract(1, 'h'),
    end: moment().startOf('minute'),
  });

  const [job, setJob] = useState<Job>();
  const [jobHistoryJobId, setJobHistoryJobId] = useState<number>();
  const [running, setRunning] = useState(false);
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [showJobPanel, setShowJobPanel] = useState(false);

  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectors(event.target.value.split('|'));
  };

  const runJob = async () => {
    setRunning(true);
    try {
      const request: EnqueueJobRequest = {
        type: JOB_TYPE,
        options: {
          startTime: timeRange.start.toISOString(),
          endTime: timeRange.end.toISOString(),
          selectors,
        },
      };
      const data = await submitJob(request);
      setJob(data!.enqueueJob);
      if (!showJobHistory) {
        setShowJobPanel(true);
        setJobHistoryJobId(undefined);
      } else {
        setJobHistoryJobId(data!.enqueueJob.id);
      }
    } catch (error) {
      console.error(`[runJob] error: ${JSON.stringify(error)}`);
      setSnackConfig(new SnackConfig({ open: true, message: JSON.stringify(error) }));
    }
    setRunning(false);
  };

  useEffect(() => {
    if (showJobHistory) {
      setShowJobPanel(false);
    } else {
      setJobHistoryJobId(undefined);
    }
  }, [showJobHistory]);

  const showHistory = () => {
    setShowJobHistory((prev) => !prev);
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.overview}>
        <h2 className={classes.title}>Alarm Triaging </h2>
        <SpinnerOverlay active={running}>
          <div>
            <CustomAndRelativeDateTimeRangePicker
              updateTimeRange={setTimeRange}
              initialTimeRange={timeRange}
              rangePickerName="Set Time Range"
            />
          </div>
          <div className={classes.spacing}>
            <InputLabel htmlFor="search-query">Search query</InputLabel>
            <OutlinedInput
              value={selectors.join('|')}
              onChange={handleChange}
              inputProps={{
                name: 'searchQuery',
                id: 'search-query',
              }}
              fullWidth
              labelWidth={60}
            />
          </div>
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={runJob}
              disabled={selectors.length === 0 || running}
              className={classes.button1}
            >
              Start Ranking
            </Button>
            <Button
              variant={showJobHistory ? 'contained' : 'outlined'}
              size="small"
              color="primary"
              onClick={showHistory}
              className={classes.button2}
            >
              Ranking History
            </Button>
          </div>
        </SpinnerOverlay>
      </Paper>
      {showJobPanel && job && (
        <Paper className={classes.overview}>
          <List>
            <OnlineMetricsJob job={job} transformResults={transformResults} columns={columns} enableCloning={false} />
          </List>
        </Paper>
      )}
      {showJobHistory && (
        <Paper className={classes.overview}>
          <OnlineMetricsJobResults
            jobId={jobHistoryJobId}
            jobTypes={[JOB_TYPE]}
            transformResults={transformResults}
            columns={columns}
            actions={actions}
          />
        </Paper>
      )}
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default RelevanceRanking;
